/**
 * Reminder pop-up shown once each time the driver opens the app.
 * Reinforces that the in-app inspection/checklist is the first thing at pickup,
 * plus the core expectations from the onboarding + monthly check-in texts.
 *
 * Monochrome (black/white) to match the Commonplace driver-app design system.
 */
import React from 'react';
import {
  Modal, View, Text, StyleSheet, ScrollView, TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, radii, fonts, RD } from '../theme/colors';

interface Props {
  visible: boolean;
  onDismiss: () => void;
}

const EXPECTATIONS: { icon: keyof typeof Ionicons.glyphMap; text: string }[] = [
  { icon: 'time-outline', text: 'Arrive on time, communicate clearly, and treat every customer with kindness and respect.' },
  { icon: 'cube-outline', text: "Bring your own vehicle, equipment, and any helpers the job needs. Make sure you're set before accepting." },
  { icon: 'checkmark-done-outline', text: 'Follow every pickup and delivery step in the app, in order.' },
  { icon: 'home-outline', text: 'A successful delivery means: item delivered, assembled if needed, working properly, the delivery checklist completed and signed by the customer, and the home left with no damage.' },
  { icon: 'cash-outline', text: 'Payment goes out as soon as the delivery is successful.' },
];

export function PickupReminderModal({ visible, onDismiss }: Props) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onDismiss} statusBarTranslucent>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scroll} bounces={false}>
            <Text style={styles.eyebrow}>BEFORE YOU START</Text>
            <Text style={styles.title}>A few quick reminders</Text>

            <View style={styles.firstThing}>
              <View style={styles.firstThingHead}>
                <Ionicons name="clipboard-outline" size={18} color={RD.ink} />
                <Text style={styles.firstThingTitle}>First thing at every pickup</Text>
              </View>
              <Text style={styles.firstThingBody}>
                Complete the in-app inspection and checklist before you load or move the item. Photos, condition, and the checklist come first, every single time.
              </Text>
            </View>

            <View style={styles.custExp}>
              <View style={styles.firstThingHead}>
                <Ionicons name="heart-outline" size={18} color={RD.ink} />
                <Text style={styles.firstThingTitle}>Customer experience comes first</Text>
              </View>
              <Text style={styles.firstThingBody}>
                A successful delivery is about more than moving an item from point A to point B. Providing an exceptional customer experience is one of the most important parts of the job. Be punctual, communicate clearly, treat customers with kindness and respect, and stay professional in every interaction. The drivers who consistently provide great service are the ones customers, partners, and our team enjoy working with most. Take pride in every delivery and every customer interaction.
              </Text>
            </View>

            <Text style={styles.expLabel}>WHAT WE EXPECT ON EVERY JOB</Text>
            {EXPECTATIONS.map((e, i) => (
              <View key={i} style={[styles.expRow, i === 0 && styles.expRowFirst]}>
                <Ionicons name={e.icon} size={20} color={RD.ink} style={styles.expIcon} />
                <Text style={styles.expText}>{e.text}</Text>
              </View>
            ))}

            <TouchableOpacity style={styles.btn} onPress={onDismiss} activeOpacity={0.85}>
              <Text style={styles.btnText}>Got it, let's go</Text>
            </TouchableOpacity>
            <Text style={styles.eachTime}>You'll see this each time you open the app.</Text>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(10,10,10,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    maxHeight: '88%',
    backgroundColor: RD.surface,
    borderRadius: radii.xxl,
    borderWidth: 1,
    borderColor: RD.border,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
  },
  scroll: { padding: spacing.xl },
  eyebrow: {
    fontSize: 11,
    letterSpacing: 1.2,
    color: RD.textFaint,
    fontFamily: fonts.semiBold,
    fontWeight: '700',
  },
  title: {
    fontSize: 24,
    color: RD.text,
    fontFamily: fonts.displayBold,
    fontWeight: '800',
    marginTop: 4,
    marginBottom: spacing.lg,
  },
  firstThing: {
    backgroundColor: RD.g03,
    borderLeftWidth: 3,
    borderLeftColor: RD.ink,
    borderRadius: radii.md,
    padding: spacing.base,
    marginBottom: spacing.lg,
  },
  custExp: {
    backgroundColor: RD.g03,
    borderLeftWidth: 3,
    borderLeftColor: RD.maroon || RD.ink,
    borderRadius: radii.md,
    padding: spacing.base,
    marginBottom: spacing.lg,
  },
  firstThingHead: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  firstThingTitle: {
    fontSize: 15,
    color: RD.ink,
    fontFamily: fonts.heavy,
    fontWeight: '800',
  },
  firstThingBody: {
    fontSize: 14,
    lineHeight: 21,
    color: RD.textSec,
    fontFamily: fonts.regular,
  },
  expLabel: {
    fontSize: 11,
    letterSpacing: 0.8,
    color: RD.textFaint,
    fontFamily: fonts.semiBold,
    fontWeight: '700',
    marginBottom: spacing.md,
  },
  expRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: RD.border,
  },
  expRowFirst: { borderTopWidth: 0, paddingTop: 0 },
  expIcon: { marginTop: 1 },
  expText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: RD.text,
    fontFamily: fonts.regular,
  },
  btn: {
    backgroundColor: RD.ink,
    borderRadius: 16,
    height: 54,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.xl,
  },
  btnText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: fonts.semiBold,
    fontWeight: '600',
  },
  eachTime: {
    textAlign: 'center',
    fontSize: 12,
    color: RD.textFaint,
    fontFamily: fonts.regular,
    marginTop: spacing.md,
  },
});
